import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import FaqCategoryContainer from '@components/FAQ/FaqCategoryContainer/FaqCategoryContainer';
import { getWebpageTemplateContent } from '@services/contentfulService';
import { staticCategories } from '@utils/faqUtils';
import FaqPageSection from '@components/FAQ/FaqPageSection/FaqPageSection';
import PreFooterSection from '@components/Shared/PreFooterSection/PreFooterSection';
import StructuredData from '@components/FAQ/StructuredData/StructuredData';

const getCurrentCategory = (slug) =>
  staticCategories.filter((c) => c.categorySlug === slug)[0];

const CategoryPage = ({ pageInfo, slug }) => {
  let faqData;
  const categoryData = pageInfo?.mainContent?.filter(
    (section) => section.faqLayout === 'Category Page'
  )[0];

  // Assign an active property to each FAQ for the open/closed display
  categoryData?.faqs?.forEach((faq) => {
    faq.active = false;
  });

  // Match the slug with the FAQ and set the active property to true
  // Then assign the faqData to update the metadata
  if (slug) {
    categoryData?.faqs?.forEach((faq) => {
      if (faq.slug.includes(slug.toLowerCase())) {
        faq.active = true;
      }
    });
    faqData = categoryData?.faqs?.filter((faq) => faq.slug === `/${slug}/`)[0];
  }

  // Handle scrolling down the page to the open FAQ
  // Only scroll if the FAQ is lower than the current scroll position
  useEffect(() => {
    if (slug) {
      const target = document.querySelector(`#${slug}`);

      window.scrollTo({
        top: target?.getBoundingClientRect().top + window.scrollY,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [slug]);

  return (
    <>
      {categoryData?.faqs ? (
        <StructuredData faqData={categoryData?.faqs} />
      ) : null}
      {categoryData ? (
        <FaqCategoryContainer
          section={categoryData}
          heroSection={pageInfo?.heroSection}
          faqData={faqData}
          metaData={{
            titleTag: pageInfo?.titleTag,
            metaDescription: pageInfo?.metaDescription,
            ogImage: pageInfo?.ogImage,
          }}
        />
      ) : null}
      <section className="faq-popularfaqs">
        {pageInfo?.mainContent?.map((category, i) =>
          category.faqLayout === 'Page Section' ? (
            <FaqPageSection
              key={`faq-popularfaqs-${i + 1}`}
              category={category}
            />
          ) : null
        )}
      </section>
      <section className="faq-prefooter">
        {pageInfo?.preFooterActions ? (
          <PreFooterSection preFooterActions={pageInfo.preFooterActions} />
        ) : null}
      </section>
    </>
  );
};

export default CategoryPage;

export async function getServerSideProps({ params }) {
  // The params come in as an array named 'slug' which can be a single value (i.e. the faq category)
  // or two values (the faq category and the faq slug).
  // For example:
  //    slug: [ 'manufactured-home-features' ]
  //    slug: [ 'manufactured-home-features', 'manufactured-homes-drywall' ]
  let category = params.slug?.[0];
  let slug = params.slug?.[1] || null; // Can't use undefined or we get an error. Error message says ok to use null or omit.
  const currentCategory = getCurrentCategory(`/${category}`);
  const { webPageKey = null } = currentCategory || {};

  if (!currentCategory) {
    return {
      notFound: true,
    };
  }

  let webPageContent;

  await Promise.all([
    getWebpageTemplateContent(webPageKey)
      .then((response) => {
        webPageContent = response;
      })
      .catch(() => {
        console.error('no content');
      }),
  ]);

  return {
    props: {
      pageInfo: webPageContent,
      slug,
    },
  };
}

CategoryPage.propTypes = {
  pageInfo: PropTypes.object,
  slug: PropTypes.string,
};
